import type { SelectionAsRecord } from '@/components/Calendar/typings'

import {
  addDays,
  addMonths,
  eachDayOfInterval,
  format,
  parse,
  subDays,
  subMonths,
  subYears,
} from 'date-fns/esm'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { isGpsOnly, selectUserConfiguration } from '@/features/domain/user'
import { timezoneUtils } from '@/server-data'

/**
 * Computes disabled days for the territory calendar based on planning range and user type
 */
function getDisabledDays(
  userType: string,
  planningRangeEndDate: Date,
  maxDate: Date,
  minDate: Date,
  isSimulation: boolean,
  firstNonArchivedDay: Date | undefined,
): SelectionAsRecord {
  const disabledDays: SelectionAsRecord = {}

  // Disable days beyond the planning range (applies to all user types)
  const planningRangeInterval = eachDayOfInterval({
    start: planningRangeEndDate,
    end: maxDate,
  })

  if (!isSimulation) {
    for (const day of planningRangeInterval) {
      disabledDays[format(day, 'yyyyMMdd')] = 1
    }
  }

  // For non-courier users or when no archived data exists, return planning range disabled days only
  if (userType !== 'courier' || !firstNonArchivedDay) {
    return disabledDays
  }

  // For courier users, also disable archived days (days before their start date)
  const archivedDaysInterval = eachDayOfInterval({
    start: minDate,
    end: subDays(firstNonArchivedDay, 1),
  })

  for (const day of archivedDaysInterval) {
    disabledDays[format(day, 'yyyyMMdd')] = 1
  }

  return disabledDays
}

export const useData = () => {
  const gpsOnly = useSelector(isGpsOnly)
  const userConfiguration = useSelector(selectUserConfiguration)

  const { planningRange } = useSelector(selectTerritoryLicensingLimits)

  return useMemo(() => {
    const refDate = new Date()
    const { startFromDate, planType, userType } = userConfiguration
    const isSimulation = planType === 'simulation'

    // The value for TODAY on Territory Calendar is the real value of current day calculated
    // like if the user is always in the timezone of the actual territory

    const todayDate = timezoneUtils.createDate(userConfiguration)(refDate)
    const todayAsString = format(todayDate, 'yyyyMMdd')

    const numberOfMonthsToShow: 1 | 2 = gpsOnly ? 1 : 2

    const firstNonArchivedDay = isSimulation ? undefined : parse(startFromDate, 'yyyyMMdd', refDate)
    const firstNonArchivedDayAsString = isSimulation ? undefined : startFromDate

    const minDate = isSimulation ? subYears(todayDate, 15) : subMonths(todayDate, 13)
    const maxDate = addMonths(todayDate, 24)

    // Calculate planning range end date
    const planningRangeEndDate = addDays(todayDate, planningRange + 1)

    // Calculate disabled days based on planning range and user type
    const disabledDays = getDisabledDays(
      userType,
      planningRangeEndDate,
      maxDate,
      minDate,
      isSimulation,
      firstNonArchivedDay,
    )

    return {
      today: todayDate,
      todayAsString,
      gpsOnly,
      disabledDays,
      numberOfMonthsToShow,
      maxIntervalLength: planningRange,
      minDate,
      maxDate,
      minDateAsString: format(minDate, 'yyyyMMdd'),
      maxDateAsString: format(maxDate, 'yyyyMMdd'),
      firstNonArchivedDay: firstNonArchivedDayAsString,
    }
  }, [userConfiguration, gpsOnly, planningRange])
}
